import React, { useState } from "react";
import { ArrowClockwise } from "react-bootstrap-icons";

import { useUserGroups } from "../../hooks/authHooks";
import { useCallsList } from "../../hooks/useCallsList";
import { CallFilters } from "../../types/calls.types";

import { CallRow } from "./callRow";

const LoadingState = () => (
  <div className="d-flex flex-column align-items-center justify-content-center p-5">
    <div className="spinner-grow text-primary mb-3" role="status"></div>
    <div className="text-muted fw-bold">LOADING...</div>
  </div>
);

const RegistryHeader: React.FC<{
  refreshing: boolean;
  onRefresh: () => void;
  totalCount: number;
  totalCountCapped: boolean;
}> = ({ refreshing, onRefresh, totalCount, totalCountCapped }) => (
  <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center fw-bold py-2">
    <span>
      Calls
      <span className="fw-normal text-white-50 ms-2">
        ({totalCount.toLocaleString()}
        {totalCountCapped ? "+" : ""})
      </span>
    </span>
    <button
      className="btn btn-sm btn-light d-flex align-items-center justify-content-center"
      onClick={onRefresh}
      disabled={refreshing}
      title="Refresh calls"
      style={{ width: "32px", height: "32px", borderRadius: "50%" }}
    >
      {refreshing ? (
        <span className="spinner-border spinner-border-sm text-dark"></span>
      ) : (
        <ArrowClockwise className="text-dark" />
      )}
    </button>
  </div>
);

interface CallRegistryProps {
  filters: CallFilters;
  emptyMessage?: string;
}

export const CallRegistry: React.FC<CallRegistryProps> = ({
  filters,
  emptyMessage = "No calls match these filters.",
}) => {
  const {
    calls,
    loading,
    loadingMore,
    error,
    hasMore,
    totalCount,
    totalCountCapped,
    loadMore,
    refetch,
  } = useCallsList(filters);
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);

  const userGroups = useUserGroups();
  const canPlayAudio =
    userGroups.includes("CallsAudioAccess") || userGroups.includes("GlobalAdmin");
  const canViewTranscript =
    userGroups.includes("CallsTranscriptAccess") || userGroups.includes("GlobalAdmin");

  const handleExpand = (callId: string) => {
    setExpandedCallId((prev) => (prev === callId ? null : callId));
  };

  // Re-fetch (filter change / refresh) while calls already exist - the table
  // stays mounted, but we grey it out and show an overlay spinner so it
  // doesn't look hung while the new results come in.
  const refreshing = loading && calls.length > 0;

  return (
    <div className="card shadow-sm border-0">
      <RegistryHeader
        refreshing={loading}
        onRefresh={() => {
          void refetch();
        }}
        totalCount={totalCount}
        totalCountCapped={totalCountCapped}
      />
      <div className="card-body p-0" style={{ minHeight: "200px" }}>
        {error && <div className="alert alert-danger m-3 mb-0">{error}</div>}

        {loading && calls.length === 0 ? (
          <LoadingState />
        ) : calls.length === 0 ? (
          <div className="text-center text-muted p-5">{emptyMessage}</div>
        ) : (
          <div className="position-relative">
            {refreshing && (
              <div
                className="position-absolute top-0 start-0 w-100 h-100 bg-white"
                style={{ opacity: 0.75, zIndex: 1 }}
              >
                {/* Sticky rather than centered in the overlay - a tall table
                    scrolls the true center out of the viewport, so pin the
                    spinner near the top of whatever's currently visible. */}
                <div
                  className="d-flex justify-content-center position-sticky mt-3"
                  style={{ top: "1rem" }}
                >
                  <span className="spinner-border text-primary" role="status"></span>
                </div>
              </div>
            )}
            <div
              className="table-responsive"
              style={refreshing ? { opacity: 0.4, pointerEvents: "none" } : undefined}
            >
              <table className="table table-hover align-middle mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>Date/Time</th>
                    <th>Status</th>
                    <th>Direction</th>
                    <th>Agent</th>
                    <th>Customer Number</th>
                    <th>Duration</th>
                    <th className="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((call) => (
                    <CallRow
                      key={call.call_id}
                      call={call}
                      isExpanded={expandedCallId === call.call_id}
                      onExpand={handleExpand}
                      canPlayAudio={canPlayAudio}
                      canViewTranscript={canViewTranscript}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {hasMore && (
          <div className="card-footer bg-light text-center border-top py-3">
            <button
              className="btn btn-outline-secondary fw-bold px-5"
              onClick={() => {
                void loadMore();
              }}
              disabled={loadingMore}
            >
              {loadingMore ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Loading...
                </>
              ) : (
                "Load more calls"
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
